import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, ArrowRight, Eye, EyeOff, Mail, Loader2, CircleCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    setLoading(true);
    try {
      const res = await register(form);
      if (res.requires_verification) {
        setPending(res);
        toast.success('Bitte bestätige jetzt deine E-Mail-Adresse');
      } else {
        toast.success('Konto erfolgreich erstellt');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await authAPI.resendVerification(form.email);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erneutes Senden fehlgeschlagen');
    } finally {
      setResending(false);
    }
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="min-h-screen bg-background flex" data-testid="register-page">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-primary/5" />
        <div className="relative z-10 text-center px-12">
          <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8 neon-glow">
            <Activity className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-5xl mb-4 tracking-tight">
            LAIzy<span className="text-primary">Finance</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-md mx-auto leading-relaxed">
            Starten Sie jetzt mit intelligentem Controlling. OCR-Rechnungserkennung, Steuererklärungs-Export und mehr.
          </p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center neon-glow">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <h1 className="font-heading font-extrabold text-2xl">
              LAIzy<span className="text-primary">Finance</span>
            </h1>
          </div>

          {pending ? (
            <div className="space-y-5" data-testid="verification-pending">
              <CircleCheck className="w-10 h-10 text-emerald-400" />
              <h2 className="font-heading font-bold text-3xl">Fast fertig!</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Wir haben eine Bestätigungs-E-Mail an <span className="text-foreground">{pending.email}</span> geschickt.
                Klicke auf den Link in der E-Mail, um dein Konto zu aktivieren. Der Link ist 48 Stunden gültig.
              </p>
              {pending.email_sent === false && (
                <p className="text-xs text-amber-400">
                  Hinweis: Der E-Mail-Versand ist gerade nicht möglich. Bitte fordere den Link später erneut an.
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleResend}
                disabled={resending}
                className="w-full h-12"
                data-testid="register-resend-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                E-Mail erneut senden
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login-link">
                  Zur Anmeldung
                </Link>
              </p>
            </div>
          ) : (
          <>
          <div className="mb-8">
            <h2 className="font-heading font-bold text-3xl mb-2">Konto erstellen</h2>
            <p className="text-muted-foreground">Registrieren Sie sich kostenlos.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Vollständiger Name</Label>
              <Input id="name" placeholder="Max Mustermann" value={form.name} onChange={update('name')} className="h-12 bg-background" required data-testid="register-name-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Unternehmen (optional)</Label>
              <Input id="company" placeholder="Muster GmbH" value={form.company_name} onChange={update('company_name')} className="h-12 bg-background" data-testid="register-company-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" placeholder="name@firma.de" value={form.email} onChange={update('email')} className="h-12 bg-background" required data-testid="register-email-input" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Passwort</Label>
              <div className="relative">
                <Input id="password" type={showPw ? 'text' : 'password'} placeholder="Min. 8 Zeichen" value={form.password} onChange={update('password')} className="h-12 bg-background pr-10" required data-testid="register-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 font-bold tracking-wider uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-[0_0_15px_rgba(0,229,255,0.3)]"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Wird erstellt...' : 'Konto erstellen'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Bereits ein Konto?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="go-to-login-link">
              Jetzt anmelden
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
